import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '@/hooks/useAuth';
import { useTelegram } from '@/hooks/useTelegram';
import { MeditationTimer } from '@/components/activities/MeditationTimer';
import { BreathingExercise } from '@/components/activities/BreathingExercise';
import { ActivityResultModal } from '@/components/activities/ActivityResultModal';
import { SkillTree } from '@/components/character/SkillTree';
import { CharacterPage } from '@/pages/Character/CharacterPage';
import { StagesPage } from '@/pages/Stages/StagesPage';
import { JournalPage } from '@/pages/Journal/JournalPage';
import { BatteryB } from '@/pages/Onboarding/BatteryB';
import { BatteryC } from '@/pages/Onboarding/BatteryC';
import { QuestsPage } from '@/pages/Quests/QuestsPage';
import { GuildsPage } from '@/pages/Social/GuildsPage';
import { EventsPage } from '@/pages/Events/EventsPage';
import { AdminPage } from '@/pages/Admin/AdminPage';
import { SubscriptionPage } from '@/pages/Subscription/SubscriptionPage';
import { SettingsPage } from '@/pages/Settings/SettingsPage';
import { ChroniclePage } from '@/pages/Chronicle/ChroniclePage';
import { ReflectionPage } from '@/pages/Reflection/ReflectionPage';
import {
  logMeditation,
  logBreathing,
  getDailyStats,
  getFatigue,
  type ActivityResult,
  type DailyStats,
  type FatigueInfo,
} from '@/services/activities';
import { getCityState, applyCityState, isReflectionTime, type CityState } from '@/utils/cityVisuals';
import { CitySilhouette } from '@/components/city/CitySilhouette';
import { BottomNav } from '@/components/navigation/BottomNav';
import { getLatestChronicleEntry, type ChronicleEntry } from '@/services/chronicle';
import { getCharacterProfile, type CharacterProfile } from '@/services/character';
import { getBatteryCResult, type BatteryCResult } from '@/services/batteryC';
import { ARCHETYPE_INFO, ARCHETYPE_V2_INFO } from '@/types';
import type { Archetype, Skill } from '@/types';

type ActiveScreen = 'home' | 'meditation' | 'breathing' | 'reflection' | 'skills' | 'character' | 'stages' | 'journal' | 'battery-b' | 'battery-c' | 'battery-b-result' | 'battery-c-result' | 'quests' | 'guilds' | 'events' | 'subscription' | 'settings' | 'admin' | 'chronicle';

export function HomePage() {
  const { user, character, setCharacter, resetOnboarding } = useAuth();
  const { hapticFeedback } = useTelegram();
  
  const [activeScreen, setActiveScreen] = useState<ActiveScreen>('home');
  const [dailyStats, setDailyStats] = useState<DailyStats | null>(null);
  const [profile, setProfile] = useState<CharacterProfile | null>(null);
  const [fatigue, setFatigue] = useState<FatigueInfo | null>(null);
  const [latestChronicle, setLatestChronicle] = useState<ChronicleEntry | null>(null);
  const [activityResult, setActivityResult] = useState<ActivityResult | null>(null);
  const [lastActivityType, setLastActivityType] = useState<string>('');
  const [showResult, setShowResult] = useState(false);
  const [showFatigueInfo, setShowFatigueInfo] = useState(false);
  const [showTooltip, setShowTooltip] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // City state based on fatigue
  const cityState: CityState = fatigue
    ? getCityState(fatigue.fatigue, fatigue.maxFatigue)
    : getCityState(0, 100);

  // Apply city state to CSS variables (based on both fatigue phase and time of day)
  useEffect(() => {
    applyCityState(cityState);
  }, [cityState.phase, cityState.timeOfDay]);

  // Загружаем профиль, статистику, усталость и летопись при монтировании
  useEffect(() => {
    loadDailyStats();
    loadProfile();
    loadFatigue();
    loadLatestChronicle();
  }, []);
  
  const loadDailyStats = async () => {
    try {
      const stats = await getDailyStats();
      setDailyStats(stats);
    } catch (error) {
      console.error('Failed to load daily stats:', error);
    }
  };

  const loadProfile = async () => {
    try {
      const p = await getCharacterProfile();
      setProfile(p);
    } catch (error) {
      console.error('Failed to load profile:', error);
    }
  };

  const loadFatigue = async () => {
    try {
      const f = await getFatigue();
      setFatigue(f);
    } catch (error) {
      console.error('Failed to load fatigue:', error);
    }
  };

  const loadLatestChronicle = async () => {
    try {
      const entry = await getLatestChronicleEntry();
      setLatestChronicle(entry);
    } catch (error) {
      console.error('Failed to load latest chronicle:', error);
    }
  };
  
  const handleMeditationComplete = async (durationMinutes: number) => {
    setIsLoading(true);
    try {
      console.log('=== CALLING logMeditation ===', durationMinutes);
      const result = await logMeditation(durationMinutes);
      console.log('=== MEDITATION RESULT ===', result);
      setActiveScreen('home');
      setActivityResult(result);
      setLastActivityType('meditation');
      setShowResult(true);

      if (result.levelUp && character) {
        setCharacter({ ...character, level: result.newLevel });
      }

      if (result.fatigueAfter !== undefined && fatigue) {
        setFatigue({ ...fatigue, fatigue: result.fatigueAfter });
      }

      loadDailyStats();
      loadLatestChronicle();
      hapticFeedback('success');
    } catch (error) {
      console.error('Failed to log meditation:', error);
      setActiveScreen('home');
      hapticFeedback('error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBreathingComplete = async (durationMinutes: number, technique: string) => {
    setIsLoading(true);
    try {
      console.log('=== CALLING logBreathing ===', durationMinutes, technique);
      const result = await logBreathing(durationMinutes, technique);
      console.log('=== BREATHING RESULT ===', result);
      setActiveScreen('home');
      setActivityResult(result);
      setLastActivityType('breathing');
      setShowResult(true);

      if (result.levelUp && character) {
        setCharacter({ ...character, level: result.newLevel });
      }

      if (result.fatigueAfter !== undefined && fatigue) {
        setFatigue({ ...fatigue, fatigue: result.fatigueAfter });
      }

      loadDailyStats();
      loadLatestChronicle();
      hapticFeedback('success');
    } catch (error) {
      console.error('Failed to log breathing:', error);
      setActiveScreen('home');
      hapticFeedback('error');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Архетип: берём из профиля (свежий fetch), fallback на ARCHETYPE_V2_INFO, затем на старую систему
  const profileArchetypeCode = profile?.archetypes?.primary;
  const archetypeV2 = profileArchetypeCode
    ? ARCHETYPE_V2_INFO[profileArchetypeCode]
    : null;
  // display_name из JOIN с таблицей archetypes (самый надёжный источник)
  const archetypeDisplayName = profile?.archetypeDisplayName
    || archetypeV2?.displayName
    || null;
  // Fallback на старую систему если V2 ещё не заполнен
  const archetypeInfo = character?.archetype
    ? ARCHETYPE_INFO[character.archetype as Archetype]
    : null;

  // Этап из профиля
  const currentStage = profile?.stage?.current || user?.currentStage || 1;
  
  // Показываем активный экран
  if (activeScreen === 'meditation') {
    return (
      <MeditationTimer
        onComplete={handleMeditationComplete}
        onCancel={() => setActiveScreen('home')}
      />
    );
  }
  
  if (activeScreen === 'breathing') {
    return (
      <BreathingExercise
        onComplete={handleBreathingComplete}
        onCancel={() => setActiveScreen('home')}
      />
    );
  }
  
  if (activeScreen === 'skills') {
    // TODO: Загружать навыки с сервера
    const mockSkills: Skill[] = [
      { skillKey: 'awareness', skillName: 'Осознанность', branch: 'mind', currentLevel: 2, currentXp: 45, xpToNextLevel: 60, unlocked: true, description: 'Присутствие в настоящем моменте' },
      { skillKey: 'focus', skillName: 'Фокус', branch: 'mind', currentLevel: 1, currentXp: 20, xpToNextLevel: 50, unlocked: true, description: 'Способность концентрироваться на важном' },
      { skillKey: 'reflection', skillName: 'Рефлексия', branch: 'mind', currentLevel: 0, currentXp: 0, xpToNextLevel: 50, unlocked: true, description: 'Умение анализировать свой опыт' },
      { skillKey: 'breathing', skillName: 'Дыхание', branch: 'body', currentLevel: 1, currentXp: 30, xpToNextLevel: 50, unlocked: true, description: 'Осознанное дыхание' },
      { skillKey: 'rest', skillName: 'Отдых', branch: 'body', currentLevel: 0, currentXp: 0, xpToNextLevel: 50, unlocked: true, description: 'Качественное восстановление' },
      { skillKey: 'movement', skillName: 'Движение', branch: 'body', currentLevel: 0, currentXp: 0, xpToNextLevel: 50, unlocked: false, description: 'Связь с телом через движение' },
      { skillKey: 'empathy', skillName: 'Эмпатия', branch: 'connections', currentLevel: 0, currentXp: 0, xpToNextLevel: 50, unlocked: true, description: 'Способность чувствовать других' },
      { skillKey: 'openness', skillName: 'Открытость', branch: 'connections', currentLevel: 0, currentXp: 0, xpToNextLevel: 50, unlocked: true, description: 'Готовность к новым связям' },
      { skillKey: 'goals', skillName: 'Цели', branch: 'meaning', currentLevel: 0, currentXp: 0, xpToNextLevel: 50, unlocked: true, description: 'Ясность в целях и намерениях' },
      { skillKey: 'values', skillName: 'Ценности', branch: 'meaning', currentLevel: 0, currentXp: 0, xpToNextLevel: 50, unlocked: true, description: 'Понимание своих ценностей' },
    ];
    
    return (
      <SkillTree
        skills={mockSkills}
        characterLevel={character?.level || 1}
        onClose={() => setActiveScreen('home')}
      />
    );
  }
  
  if (activeScreen === 'character') {
    return (
      <CharacterPage
        onClose={() => setActiveScreen('home')}
        onReset={() => resetOnboarding()}
      />
    );
  }

  if (activeScreen === 'stages') {
    return (
      <StagesPage onClose={() => setActiveScreen('home')} />
    );
  }

  if (activeScreen === 'journal') {
    return (
      <JournalPage onClose={() => setActiveScreen('home')} />
    );
  }

  if (activeScreen === 'battery-b') {
    return (
      <BatteryB
        onComplete={() => setActiveScreen('home')}
        onClose={() => setActiveScreen('home')}
      />
    );
  }

  if (activeScreen === 'battery-c') {
    return (
      <BatteryC
        onComplete={() => setActiveScreen('home')}
        onClose={() => setActiveScreen('home')}
      />
    );
  }

  if (activeScreen === 'battery-b-result') {
    return <BatteryBResultScreen onClose={() => setActiveScreen('home')} />;
  }

  if (activeScreen === 'battery-c-result') {
    return <BatteryCResultScreen onClose={() => setActiveScreen('home')} />;
  }

  if (activeScreen === 'quests') {
    return (
      <QuestsPage onClose={() => setActiveScreen('home')} />
    );
  }
  
  if (activeScreen === 'guilds') {
    return (
      <GuildsPage 
        onClose={() => setActiveScreen('home')} 
        currentUserId={user?.id}
      />
    );
  }
  
  if (activeScreen === 'events') {
    return (
      <EventsPage onClose={() => setActiveScreen('home')} />
    );
  }

  if (activeScreen === 'admin') {
    return (
      <AdminPage onClose={() => setActiveScreen('home')} />
    );
  }

  if (activeScreen === 'subscription') {
    return (
      <SubscriptionPage onClose={() => setActiveScreen('home')} />
    );
  }
  
  if (activeScreen === 'settings') {
    return (
      <SettingsPage onClose={() => setActiveScreen('home')} />
    );
  }

  if (activeScreen === 'chronicle') {
    return (
      <ChroniclePage onClose={() => setActiveScreen('home')} />
    );
  }

  if (activeScreen === 'reflection') {
    return (
      <ReflectionPage
        onClose={() => setActiveScreen('home')}
        onComplete={() => {
          loadLatestChronicle();
          loadDailyStats();
          loadFatigue();
          setActiveScreen('home');
        }}
      />
    );
  }

  // Home screen
  return (
    <div
      className={`min-h-screen safe-area-top safe-area-bottom relative city-adaptive city-${cityState.phase}`}
      style={{ background: cityState.skyGradient }}
    >
      {/* Aurora effect (visible when city is alive/shining) */}
      <div className="aurora-container">
        <div className="aurora" />
        <div className="aurora aurora-2" />
      </div>

      {/* Twinkling stars background */}
      <div className="stars-container">
        <div className="star star-1" />
        <div className="star star-2" />
        <div className="star star-3" />
        <div className="star star-4" />
        <div className="star star-5" />
        <div className="star star-6" />
        <div className="star star-7" />
        <div className="star star-8" />
        <div className="star star-9" />
        <div className="star star-10" />
        <div className="star star-11" />
        <div className="star star-12" />
        <div className="star star-13" />
        <div className="star star-14" />
        <div className="star star-15" />
      </div>

      {/* Header with moon and character info */}
      <div className="relative z-10 p-5 text-center">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-2"
        >
          <span className="text-4xl">☽</span>
        </motion.div>
        <h1 className="font-serif text-2xl text-accent-moon mb-1">
          Город Реалити
        </h1>
        <motion.div
          whileTap={{ scale: 0.95 }}
          onClick={() => setActiveScreen('character')}
          className="inline-block cursor-pointer"
        >
          <p className="text-primary-100 text-sm">
            {archetypeDisplayName || archetypeInfo?.name || 'Путник'} • Этап {currentStage}
          </p>
        </motion.div>

        {/* Settings & Premium buttons */}
        <div className="absolute top-5 right-4 flex gap-2">
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              hapticFeedback('light');
              setActiveScreen('settings');
            }}
            className="w-9 h-9 rounded-full bg-primary-800/40 border border-primary-700/30 flex items-center justify-center"
          >
            <span className="text-sm">⚙️</span>
          </motion.button>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              hapticFeedback('light');
              setActiveScreen('subscription');
            }}
            className="w-9 h-9 rounded-full bg-gradient-to-br from-accent-warm/20 to-accent-rose/20 border border-accent-warm/30 flex items-center justify-center"
          >
            <span className="text-sm">⭐</span>
          </motion.button>
        </div>
      </div>

      {/* Fatigue bar */}
      {fatigue && (
        <FatigueBar
          fatigue={fatigue.fatigue}
          maxFatigue={fatigue.maxFatigue}
          cityMessage={cityState.message}
          onClick={() => {
            hapticFeedback('light');
            setShowFatigueInfo(true);
          }}
        />
      )}

      {/* Diagnostics hint banner */}
      {user && !user.batteryCCompleted && (
        <motion.div
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-10 mx-4 mb-3 p-3 rounded-xl bg-indigo-900/20 border border-indigo-800/30 cursor-pointer"
          onClick={() => setActiveScreen(user.batteryBCompleted ? 'battery-c' : 'battery-b')}
        >
          <p className="text-xs text-indigo-300 text-center">
            🔮 {user.batteryBCompleted
              ? 'Пройди Диагностику C, чтобы завершить калибровку профиля'
              : 'Пройди Диагностику B и C, чтобы уточнить характеристики и получить детальное описание'}
          </p>
        </motion.div>
      )}

      {/* Daily stats */}
      {dailyStats && (
        <div className="relative z-10 px-4 py-3 grid grid-cols-3 gap-3">
          <StatCard
            emoji="🌙"
            value={`${dailyStats.meditationMinutes}`}
            label="мин тишины"
            onClick={() => {
              hapticFeedback('light');
              setShowTooltip('meditation');
            }}
          />
          <StatCard
            emoji="✨"
            value={`+${dailyStats.totalXp}`}
            label="XP сегодня"
            onClick={() => {
              hapticFeedback('light');
              setShowTooltip('xp');
            }}
          />
          <StatCard
            emoji="🔥"
            value={`${dailyStats.streak}`}
            label="дней подряд"
            onClick={() => {
              hapticFeedback('light');
              setShowTooltip('streak');
            }}
          />
        </div>
      )}

      {/* Chronicle widget */}
      {latestChronicle && (
        <ChronicleWidget
          narrative={latestChronicle.narrative}
          onClick={() => setActiveScreen('chronicle')}
        />
      )}

      {/* Practices with fairy-tale names */}
      <div className="relative z-10 px-4 pb-3">
        <h2 className="font-serif text-lg text-accent-moon mb-3">Практики</h2>

        <div className="grid grid-cols-2 gap-3">
          <PracticeCard
            icon="🌙"
            title="Тишина"
            subtitle="Медитация"
            fatigueHint="-30"
            onClick={() => {
              hapticFeedback('light');
              setActiveScreen('meditation');
            }}
          />
          <PracticeCard
            icon="🌬️"
            title="Ветер"
            subtitle="Дыхание"
            fatigueHint="-15"
            onClick={() => {
              hapticFeedback('light');
              setActiveScreen('breathing');
            }}
          />
          <PracticeCard
            icon="✒️"
            title="Рефлексия"
            subtitle={isReflectionTime() ? 'Вечерняя практика' : 'Доступно с 21:00'}
            fatigueHint={isReflectionTime() ? '-10' : undefined}
            onClick={() => {
              if (isReflectionTime()) {
                hapticFeedback('light');
                setActiveScreen('reflection');
              }
            }}
            disabled={!isReflectionTime()}
            lockedMessage={!isReflectionTime() ? '🕘' : undefined}
          />
          <PracticeCard
            icon="📜"
            title="Хроника"
            subtitle="Летопись"
            onClick={() => {
              hapticFeedback('light');
              setActiveScreen('chronicle');
            }}
          />
        </div>
      </div>

      {/* Quick links */}
      <div className="relative z-10 px-4 pb-6">
        <h2 className="font-serif text-lg text-accent-moon mb-3">Ещё</h2>

        <div className="grid grid-cols-4 gap-2">
          <QuickLink emoji="🧬" title="Персонаж" onClick={() => setActiveScreen('character')} />
          <QuickLink emoji="🗺️" title="Путь" onClick={() => setActiveScreen('stages')} />
          <QuickLink emoji="📖" title="Свиток" onClick={() => setActiveScreen('journal')} />
          <QuickLink emoji="👥" title="Гильдии" onClick={() => setActiveScreen('guilds')} />
          <QuickLink
            emoji="🔮"
            title="Тест B"
            completed={!!user?.batteryBCompleted}
            onClick={() => setActiveScreen(user?.batteryBCompleted ? 'battery-b-result' : 'battery-b')}
          />
          {user?.batteryBCompleted && (
            <QuickLink
              emoji="⚡"
              title="Тест C"
              completed={!!user?.batteryCCompleted}
              onClick={() => setActiveScreen(user?.batteryCCompleted ? 'battery-c-result' : 'battery-c')}
            />
          )}
          {(user?.role === 'admin' || user?.role === 'event_manager') && (
            <QuickLink emoji="🛠️" title="Админка" onClick={() => setActiveScreen('admin')} />
          )}
        </div>
      </div>

      {/* City message */}
      <div className="relative z-10 px-6 pb-4 text-center">
        <p className="city-message text-sm">{cityState.message}</p>
      </div>

      {/* City silhouette */}
      <CitySilhouette cityState={cityState} />

      {/* Bottom navigation */}
      <BottomNav
        active="home"
        onChange={(item) => {
          hapticFeedback('light');
          if (item === 'character') setActiveScreen('character');
          else if (item === 'chronicle') setActiveScreen('chronicle');
          else if (item === 'events') setActiveScreen('events');
          else if (item === 'quests') setActiveScreen('quests');
        }}
      />

      {/* Loading overlay */}
      {isLoading && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-primary-950/90 backdrop-blur-sm">
          <motion.div
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            className="text-4xl mb-4"
          >
            ✨
          </motion.div>
          <motion.p
            animate={{ opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            className="text-primary-300 text-sm font-serif"
          >
            Записываем в летопись...
          </motion.p>
        </div>
      )}

      {/* Activity result modal */}
      <ActivityResultModal
        isOpen={showResult}
        result={activityResult}
        activityType={lastActivityType}
        onClose={() => setShowResult(false)}
      />

      {/* Fatigue info modal */}
      <InfoModal
        isOpen={showFatigueInfo}
        onClose={() => setShowFatigueInfo(false)}
        title="⚡ Усталость"
      >
        <p className="text-primary-200 text-sm leading-relaxed mb-4">
          Усталость накапливается в течение дня и влияет на яркость города.
        </p>
        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-3 text-sm">
            <span className="text-lg">🌙</span>
            <span className="text-primary-300">Тишина (медитация)</span>
            <span className="text-accent-teal ml-auto">-30</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-lg">💨</span>
            <span className="text-primary-300">Ветер (дыхание)</span>
            <span className="text-accent-teal ml-auto">-15</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-lg">✒️</span>
            <span className="text-primary-300">Рефлексия</span>
            <span className="text-accent-teal ml-auto">-10</span>
          </div>
        </div>
        <p className="text-primary-400 text-xs italic">
          Чем меньше усталость — тем ярче сияет город твоего двойника.
        </p>
      </InfoModal>

      {/* Stat card tooltips */}
      <InfoModal
        isOpen={showTooltip === 'meditation'}
        onClose={() => setShowTooltip(null)}
        title="🌙 Минуты тишины"
      >
        <p className="text-primary-200 text-sm leading-relaxed">
          Сколько минут медитации ты сделал сегодня.
        </p>
        <p className="text-primary-400 text-sm mt-3">
          Попробуй начать с 5 минут — это уже меняет день.
        </p>
      </InfoModal>

      <InfoModal
        isOpen={showTooltip === 'xp'}
        onClose={() => setShowTooltip(null)}
        title="✨ Очки опыта"
      >
        <p className="text-primary-200 text-sm leading-relaxed">
          Очки опыта за сегодня.
        </p>
        <p className="text-primary-400 text-sm mt-3">
          Практики прокачивают 8 характеристик твоего персонажа: внимание, энергию тела, эмоциональную регуляцию и другие.
        </p>
      </InfoModal>

      <InfoModal
        isOpen={showTooltip === 'streak'}
        onClose={() => setShowTooltip(null)}
        title="🔥 Серия дней"
      >
        <p className="text-primary-200 text-sm leading-relaxed">
          Серия дней, когда ты делал хотя бы одну практику.
        </p>
        <p className="text-primary-400 text-sm mt-3">
          Не прерывай серию — город запоминает постоянство.
        </p>
      </InfoModal>
    </div>
  );
}

function StatCard({ emoji, value, label, onClick }: { emoji: string; value: string; label: string; onClick?: () => void }) {
  return (
    <motion.button
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      className="card text-center py-3 cursor-pointer active:bg-primary-700/40 transition-colors"
    >
      <span className="text-lg">{emoji}</span>
      <p className="text-lg font-semibold text-accent-moon">{value}</p>
      <p className="text-xs text-primary-600">{label}</p>
    </motion.button>
  );
}

interface PracticeCardProps {
  icon: string;
  title: string;
  subtitle: string;
  fatigueHint?: string;
  onClick: () => void;
  disabled?: boolean;
  lockedMessage?: string;
}

function PracticeCard({ icon, title, subtitle, fatigueHint, onClick, disabled, lockedMessage }: PracticeCardProps) {
  return (
    <motion.button
      whileTap={{ scale: disabled ? 1 : 0.97 }}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`practice-card text-left ${disabled ? 'opacity-50' : ''}`}
    >
      <div className="flex items-start justify-between mb-3">
        <span className="text-3xl">{icon}</span>
        {fatigueHint && !disabled && (
          <span className="text-xs text-accent-teal bg-accent-teal/10 px-2 py-1 rounded-full">
            {fatigueHint}
          </span>
        )}
        {disabled && (
          <span className="text-xs text-primary-600">{lockedMessage || '🔒'}</span>
        )}
      </div>
      <h3 className="font-serif text-lg text-primary-100 mb-0.5">{title}</h3>
      <p className="text-xs text-primary-600">{subtitle}</p>
    </motion.button>
  );
}

interface QuickLinkProps {
  emoji: string;
  title: string;
  onClick?: () => void;
  disabled?: boolean;
  completed?: boolean;
}

function QuickLink({ emoji, title, onClick, disabled, completed }: QuickLinkProps) {
  return (
    <motion.button
      whileTap={{ scale: disabled ? 1 : 0.95 }}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`relative p-3 rounded-xl flex flex-col items-center gap-1.5 text-center transition-all ${
        completed
          ? 'bg-primary-800/40 border border-primary-700/20 opacity-50'
          : disabled
            ? 'bg-primary-900/30 opacity-50'
            : 'bg-primary-800/40 hover:bg-primary-800/60 border border-primary-700/20'
      }`}
    >
      {completed && (
        <span className="absolute top-1 right-1 text-[9px] text-emerald-400 font-medium leading-none">✓</span>
      )}
      <span className="text-xl">{emoji}</span>
      <span className="text-xs text-primary-300">{title}</span>
    </motion.button>
  );
}

interface FatigueBarProps {
  fatigue: number;
  maxFatigue: number;
  cityMessage?: string;
  onClick?: () => void;
}

function FatigueBar({ fatigue, maxFatigue, cityMessage, onClick }: FatigueBarProps) {
  const percentage = Math.round((fatigue / maxFatigue) * 100);

  // Color thresholds per requirements: 80-100% red, 50-79% orange, 0-49% teal
  const getFatigueLevel = () => {
    if (percentage >= 80) return 'high';
    if (percentage >= 50) return 'medium';
    return 'low';
  };

  const getEmoji = () => {
    if (percentage >= 80) return '🌑';
    if (percentage >= 50) return '🌙';
    if (percentage >= 20) return '✨';
    return '☀️';
  };

  // City state messages
  const getCityMessage = () => {
    if (cityMessage) return cityMessage;
    if (percentage >= 80) return 'Город спит. Фонари погасли.';
    if (percentage >= 50) return 'Город просыпается. В окнах зажигается свет.';
    if (percentage >= 20) return 'Улицы наполняются шёпотом.';
    return 'Город сияет. Каждый фонарь — маленькое солнце.';
  };

  const level = getFatigueLevel();

  return (
    <motion.button
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="relative z-10 mx-4 mb-3 p-4 card w-[calc(100%-2rem)] text-left cursor-pointer active:bg-primary-700/30 transition-colors"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-lg">{getEmoji()}</span>
          <span className="text-sm text-primary-200 font-serif">Усталость</span>
          <span className="text-xs text-primary-500">ⓘ</span>
        </div>
        <span className="text-sm font-medium text-primary-300">
          {percentage}%
        </span>
      </div>
      <div className={`fatigue-bar fatigue-${level}`}>
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="fatigue-bar-fill"
        />
      </div>
      <p className="text-xs text-primary-500 mt-2 italic city-message">
        {getCityMessage()}
      </p>
    </motion.button>
  );
}

interface InfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

function InfoModal({ isOpen, onClose, title, children }: InfoModalProps) {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backdropFilter: 'blur(4px)', backgroundColor: 'rgba(0,0,0,0.6)' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ duration: 0.2 }}
        className="w-full max-w-sm bg-primary-900/95 border border-primary-700/50 rounded-2xl p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-serif text-lg text-white mb-4">{title}</h3>
        <div className="mb-5">{children}</div>
        <button
          onClick={onClose}
          className="w-full py-3 px-4 bg-accent-warm/20 hover:bg-accent-warm/30 text-accent-warm rounded-xl text-sm font-medium transition-colors"
        >
          Понятно
        </button>
      </motion.div>
    </div>
  );
}

interface ChronicleWidgetProps {
  narrative: string;
  onClick: () => void;
}

function ChronicleWidget({ narrative, onClick }: ChronicleWidgetProps) {
  return (
    <motion.button
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="relative z-10 mx-4 mb-4 p-4 card-glow text-left overflow-hidden"
    >
      {/* Decorative stars */}
      <div className="absolute top-3 right-3 text-accent-purple/40 animate-twinkle">✦</div>
      <div className="absolute top-6 right-8 text-accent-purple/30 animate-twinkle-slow">✧</div>
      <div className="absolute bottom-4 left-4 text-accent-purple/20 animate-twinkle">✦</div>

      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xl">📜</span>
        <span className="text-xs text-accent-warm font-serif">Летопись города</span>
      </div>

      {/* Narrative text in fairy-tale style */}
      <p className="chronicle-text line-clamp-3 leading-relaxed">
        {narrative}
      </p>

      {/* Read more link */}
      <div className="mt-3 flex items-center gap-1.5 text-xs text-accent-purple">
        <span>Открыть хронику</span>
        <span className="text-accent-warm">→</span>
      </div>
    </motion.button>
  );
}

// ============================================
// Battery Result Screens
// ============================================

function BatteryBResultScreen({ onClose }: { onClose: () => void }) {
  return (
    <div className="min-h-screen bg-primary-950 flex flex-col items-center justify-center p-6 text-center">
      <span className="text-5xl mb-4">✅</span>
      <h1 className="text-xl font-bold text-white mb-3">Диагностика B пройдена</h1>

      <div className="w-full max-w-xs mb-6 space-y-3">
        <div className="p-4 rounded-xl bg-primary-800/30 border border-primary-700/50">
          <p className="text-sm text-primary-300 mb-2">Твой профиль уточнён на основе:</p>
          <ul className="text-xs text-primary-400 space-y-1 text-left">
            <li>📖 Проективных историй по картинкам</li>
            <li>🎭 Анализа жизненных ситуаций</li>
            <li>💎 Выбранных ценностей и приоритетов</li>
            <li>⚖️ Реакций на сложные выборы</li>
          </ul>
        </div>

        <div className="p-3 rounded-xl bg-indigo-900/20 border border-indigo-800/30">
          <p className="text-xs text-indigo-300">
            Характеристики обновлены. Посмотри изменения в профиле Персонажа.
          </p>
        </div>
      </div>

      <motion.button
        whileTap={{ scale: 0.97 }}
        onClick={onClose}
        className="w-full max-w-xs py-3 rounded-xl bg-primary-800/50 border border-primary-700/30 text-white font-medium"
      >
        Назад
      </motion.button>
    </div>
  );
}

function BatteryCResultScreen({ onClose }: { onClose: () => void }) {
  const [result, setResult] = useState<BatteryCResult | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getBatteryCResult()
      .then(setResult)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-primary-950 flex items-center justify-center">
        <p className="text-primary-400 text-sm">Загрузка...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-950 flex flex-col items-center justify-center p-6 text-center">
      <span className="text-5xl mb-4">✅</span>
      <h1 className="text-xl font-bold text-white mb-3">Калибровка завершена</h1>

      {result && (
        <div className="space-y-3 mb-6 w-full max-w-xs">
          <div className="rounded-xl bg-primary-800/30 border border-primary-700/50 p-3">
            <p className="text-xs text-primary-500">Индекс состояния</p>
            <p className="text-2xl font-bold text-white">{result.stateIndex.toFixed(1)}<span className="text-sm text-primary-500">/7</span></p>
          </div>

          {result.tieBreakApplied && (
            <div className="rounded-xl bg-indigo-900/30 border border-indigo-800/30 p-3">
              <p className="text-xs text-indigo-400">Профиль уточнён с помощью калибровки</p>
            </div>
          )}
        </div>
      )}

      {!result && (
        <div className="mb-6 w-full max-w-xs">
          <div className="p-3 rounded-xl bg-primary-800/30 border border-primary-700/50">
            <p className="text-xs text-primary-400">Результаты калибровки применены к профилю</p>
          </div>
        </div>
      )}

      <motion.button
        whileTap={{ scale: 0.97 }}
        onClick={onClose}
        className="w-full max-w-xs py-3 rounded-xl bg-primary-800/50 border border-primary-700/30 text-white font-medium"
      >
        Назад
      </motion.button>
    </div>
  );
}
